/**
 * 枚举的中文标签 —— **全仓唯一一份**,与 status.ts 并列。
 *
 * 为什么必须收成一处:视图里原来写的是 `row.dispatchMode === 'pool' ? '放池' : '直派'`
 * 这样的三元表达式。它有两个毛病,而且都**不会报错**:
 *
 *   ① 多了一个取值时,新取值会被顺手归到「不是 pool 的那一边」——
 *      一个本该显示 `designated` 的字段会安静地显示成「直派」。
 *   ② 业务线、派单方式、代办阶段、货类的中文在两个端各写一份,
 *      第一个分叉(同一个取值在两处叫两个名字)没有任何人会发现。
 *
 * 所以这里的每个函数都长成同一个形状:**用 shared 的 `isXxx` 守卫判断,
 * 认不出来的取值原样返回字符串**。原样返回不是兜底,是让它难看 ——
 * 页面上出现 `ship_certificates` 一眼就知道少了一处映射,
 * 而显示成「其他」会被读成「这个数据本来就是其他」,排查方向一开始就是错的。
 * 这条行为与 status.ts 的 statusLabel 完全一致:全仓只有一种「不认识这个取值」的表现。
 *
 * ═══ 取值从哪来 ═══
 *
 * 取值集合来自 `@sea-drone/shared` 的枚举(`CARGO_CATEGORY` 等,单点定义)。
 * 本文件**不重列一遍那些字符串**,而是从 shared 取两件东西把它们钉住:
 *   · 运行期:`isCargoCategory` 这类守卫 —— 它就是 `CARGO_CATEGORY.includes(...)`,
 *     与那张数组同源(所以这里不必再 import 数组本身)
 *   · 编译期:`Record<CargoCategory, string>` 的表结构 —— shared 加了取值、
 *     改了取值名,或者这里拼错一个,typecheck 当场报红,而不是等页面少认一个
 *     (两张列表不一致的那种失效,表现只是「页面少认一个取值」,没人会报上来)
 */
import {
  ANCHORAGE_SCOPE,
  PROOF_NODE_LABEL,
  isAdminAccountKind,
  isAgencyStage,
  isCargoCategory,
  isCarrierApplicationStatus,
  isCarrierMaterialType,
  isDispatchMode,
  isIdType,
  isPricingTableStatus,
  isProofNode,
  isRefundStatus,
  isServiceType,
  isSettlementStatus,
  isTakeoffApplicationStatus,
  isTransportMode,
  type AdminAccountKind,
  type AgencyStage,
  type AnchorageScope,
  type CargoCategory,
  type CarrierApplicationStatus,
  type CarrierMaterialType,
  type DispatchMode,
  type IdType,
  type PricingTableStatus,
  type RefundStatus,
  type ServiceType,
  type SettlementStatus,
  type TakeoffApplicationStatus,
  type TransportMode,
} from '@sea-drone/shared'
import type { StatusGroup } from './status'

/**
 * 六个取值的中文取自 packages/shared/src/enums/cargoCategory.ts 的文件头
 * (「食品 / 纸张文书 / 船舶证书 / 机械配件 / 日用品 / 其他」)——
 * 那是这一组译名第一次出现的地方,不另起一套。
 */
export const CARGO_LABELS: Record<CargoCategory, string> = {
  food: '食品',
  documents: '纸张文书',
  ship_certificates: '船舶证书',
  machinery_parts: '机械配件',
  daily_goods: '日用品',
  other: '其他',
}

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  transport: '纯运输',
  full_agency: '全流程代办',
}

/** 沿用的就是视图里原来那两个词(「放池」/「直派」),不另起译名 */
export const DISPATCH_MODE_LABELS: Record<DispatchMode, string> = {
  designated: '直派',
  pool: '放池',
}

export const AGENCY_STAGE_LABELS: Record<AgencyStage, string> = {
  processing: '办理中',
  done: '已办完',
}

/**
 * 退款单状态(S-42 加)。四个中文名第一次需要出现在界面上 ——
 * 运营端订单驾驶舱(O1)的抽屉里「退款状态」那一格。
 *
 * 这张表此前**不存在**,而且 refundStatus.ts 一直挂在 enum-labels 的**豁免表**里,
 * 理由原文是「客户在小程序看退款到哪里了,运营端看的是订单状态(refunding / refunded)
 * —— 后者是给客户的答案,前者是内部账」。S-42 的 O1 画板把「退款状态」画成了
 * 详情里的一个字段,那句话因此不再成立:一个字段名配一个英文码
 * (`success` / `failed`)正是枚举守卫要消灭的形态。所以它从豁免表挪进登记表。
 *
 * 三个词取自画板那一格的数据候选(pending → 待退款 / processing → 退款中 /
 * success → 已退款);failed 画板上没有(那一格画的是 pending),取 shared 枚举
 * 文件头对它的解释:**微信明确说这笔退款失败,钱还在平台账上,必须有人去处理** ——
 * 「退款失败」是它唯一的读法。这四个词与契约 RefundStatus 的四条 description 一一对应。
 *
 * 与订单状态那两个词的分工:订单落 refunding / refunded 说的是「这一单现在在哪」,
 * 这张表说的是「退款单走到哪一步」—— 后者答得出 processing(已受理、等通知)
 * 与 failed(要人去处理)的区别,而订单状态那一层答不出来。
 */
export const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  pending: '待退款',
  processing: '退款中',
  success: '已退款',
  failed: '退款失败',
}

export function refundStatusLabel(value: string): string {
  return isRefundStatus(value) ? REFUND_STATUS_LABELS[value] : value
}

/**
 * 结算流水状态(S-43 加)。四个中文名第一次需要出现在界面上 ——
 * 运营端结算页(v2 · O2)的行标签与第一张统计卡。
 *
 * 这张表此前不存在,而 settlementStatus.ts 一直挂在 enum-labels 的**豁免表**里,
 * 理由原文是「这个仓库里没有结算页面,对账走的是线下对公转账」。O2 就是那个页面,
 * 那句理由从此不成立 —— 一列状态配英文码(payable / reversed)正是枚举守卫要消灭的形态。
 *
 * 四个词的取法:
 *   · payable →「待结算」:画板上那一行的标签就是它,而且它说的正是这个值的事 ——
 *     流水建出来了、承运商还等着这笔钱。**结算页那张「待结算」统计卡也取这一个词**:
 *     统计卡与行标签说的是同一件事,两边各写一份就是下一个分叉点。
 *   · reversed →「已冲正」:取消退款链路写下的冲正(Y-19)。画板的样例行没有它
 *     (那几单没退过款),词按 shared 枚举文件头的解释取。
 *   · paid_out →「已结算(本期不产出)」、pending →「待定金额(不产出)」:
 *     本期没有任何路径产出这两个取值 —— 打款走线下对公转账(结算是单段式的,
 *     不走「付款即建流水、完成再定金额」那两段)。括号注解沿用 ORDER_STATUS_LABELS 里
 *     「已创建(不产出)」那一族的写法:它交代的是「这个值会不会出现」,
 *     演示时被问到要答得上来 —— 而那正是结算状态文件里那句「『刻意从不出现』与
 *     『本该出现但漏了』在数据里长得一模一样」(ESC-004 的机制)在界面上的答案。
 */
export const SETTLEMENT_STATUS_LABELS: Record<SettlementStatus, string> = {
  pending: '待定金额(不产出)',
  payable: '待结算',
  paid_out: '已结算(本期不产出)',
  reversed: '已冲正',
}

export function settlementStatusLabel(value: string): string {
  return isSettlementStatus(value) ? SETTLEMENT_STATUS_LABELS[value] : value
}

/**
 * 结算状态的颜色分组(照 CARRIER_APPLICATION_STATUS_GROUPS 写)。
 *
 * payable 是「还等我们打款」→ attention(橙;画板上那一行的「待结算」标签也是琥珀);
 * paid_out 是打完了 → done(绿;画板的「已结算」是绿的);reversed 与订单的
 * cancelled / refunded 同一档 → draft(灰):这笔账已经冲平、不需要谁再动手。
 * pending 永不出现,归 attention —— 哪天真出现它,说明走回了两段式,那时该有人看。
 */
export const SETTLEMENT_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  pending: 'attention',
  payable: 'attention',
  paid_out: 'done',
  reversed: 'draft',
}

export function settlementStatusGroup(value: string): StatusGroup {
  return SETTLEMENT_STATUS_GROUPS[value] ?? 'draft'
}

export function cargoLabel(code: string): string {
  return isCargoCategory(code) ? CARGO_LABELS[code] : code
}

export function serviceTypeLabel(value: string): string {
  return isServiceType(value) ? SERVICE_TYPE_LABELS[value] : value
}

/**
 * 两条业务线的一句话说明(客户首页「两条业务线」卡、下单页的选项副标题)。
 *
 * 与 SERVICE_TYPE_LABELS 分开、而不是合成一张表:标签是两个词(订单卡片的状态行
 * 里用),说明是一整句(业务线的介绍里用)—— 合成一张会逼着两个场景共用一种长度。
 * 取值集合相同,所以这里的类型同样钉在 shared 的 ServiceType 上:shared 多一个取值
 * 而这里没跟上,typecheck 当场报红(与上面那几张表同一条纪律)。
 *
 * ═══ 2026-10-16 按 AS-072③ 换过措辞 ═══
 *
 * AS-072③(已裁决)把这两句定为**我们自己的文案**并逐字引用了它们;
 * 画板 v2 · M2(客户首页)用的就是这一版整句。v2 · M4(下单页)的选择卡用的是
 * 压缩形态「手续你已自办」「海关边检我们办」—— S-35 把它们落成下面那张
 * SERVICE_TYPE_OPTION_DESC(同一族的短句,不是这两句的改写)。
 * 改动前的旧句子里带着「浙江益达」这个名字 —— 它当时是那个名字在代码里的
 * 唯一落点;新句子不再提它。将来平台配置里真的要有自营承运商的名字时,
 * 新增一处引用,不要在某一句说明里再写一遍。
 *
 * 这两句话**只住在这里**:客户首页与下单页都调 serviceTypeDesc(),
 * 页面里手写一份逐字副本就是把「同一句话两处各有一份」请回来。
 */
export const SERVICE_TYPE_DESC: Record<ServiceType, string> = {
  transport: '手续你已自办,我们只管运',
  full_agency: '海关边检手续我们代办,顺带把货送到',
}

export function serviceTypeDesc(value: string): string {
  return isServiceType(value) ? SERVICE_TYPE_DESC[value] : value
}

/**
 * 两条业务线在**选择卡上的短说明**(v2 · M4 下单页的实物,2026-10-16 加)。
 *
 * 与上面那张长句表分开、而不是替换它:首页那张通栏卡给的是整句
 * (「手续你已自办,我们只管运」),而下单页那两张半宽卡只有一句的空间 ——
 * 画板 v2 · M4 上写的就是压缩形态(「手续你已自办」「海关边检我们办」)。
 * 同一件事在两种长度上各有一句,与 LABELS(两个词)/ DESC(一整句)分开的理由一样:
 * 合成一张会逼着两个场景共用一种长度。
 *
 * 它**只住在这里**:下单页的选项卡调 serviceTypeOptionDesc(),页面里手写一份
 * 逐字副本就是把「同一句话两处各有一份」请回来(design-copy.json 的 M4 帧
 * 把这两句登记成 copyFrom 指向本文件)。
 */
export const SERVICE_TYPE_OPTION_DESC: Record<ServiceType, string> = {
  transport: '手续你已自办',
  full_agency: '海关边检我们办',
}

export function serviceTypeOptionDesc(value: string): string {
  return isServiceType(value) ? SERVICE_TYPE_OPTION_DESC[value] : value
}

export function dispatchModeLabel(value: string): string {
  return isDispatchMode(value) ? DISPATCH_MODE_LABELS[value] : value
}

/**
 * 代办阶段。**空值返回一个破折号**,与 FieldGrid 的画法一致 ——
 * 纯运输单的 agencyStage 恒为 null(M-01:「没有代办阶段」与「代办阶段是办理中」
 * 是两件事)。返回空串会让单元格空着,而空白读起来像「这里没有这一项」。
 */
export function agencyStageLabel(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return isAgencyStage(value) ? AGENCY_STAGE_LABELS[value] : value
}

/**
 * 凭证节点名。**这四个名字的定义在 shared**(proofNode.ts 的 PROOF_NODE_LABEL):
 * 服务端的错误话术里也要用它们(M-18:飞手读到的不该是 drone_returned),
 * 在这里再抄一份就等于让同一句话有两个版本。所以这里只做未知值那一层。
 */
export function proofNodeLabel(node: string): string {
  return isProofNode(node) ? PROOF_NODE_LABEL[node] : node
}

/**
 * 运输段模式(L0-03)。三个取值写在 shared 的 enums/transportMode.ts 里;
 * 陆运 / 海运两个词取自那个文件的文件头(「陆运与海运由承运商自己包圆」),
 * 空运是订单详情抽屉里原来就在用的那个词。
 *
 * road / sea 是预留取值,当前一行代码都不产出它们 —— 表格里真的出现「陆运」那天,
 * 说明开始拆段了,而这次至少不会有人以为那是数据错了。
 */
export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  road: '陆运',
  air: '空运',
  sea: '海运',
}

export function transportModeLabel(value: string): string {
  return isTransportMode(value) ? TRANSPORT_MODE_LABELS[value] : value
}

/**
 * 锚地内 / 外(M-02 的两套基价表)。
 *
 * 取值**不在独立枚举文件里**:ANCHORAGE_SCOPE 与计价科目码挤在
 * shared 的 enums/priceItemCode.ts 里(它俩都只被计价链路用到)。这里直接问那个数组,
 * 不重列一遍 `inside` / `outside` —— 重列的代价是取值改名或增删时这一份不跟着动,
 * 而表现只是「页面少认一个取值」,没人会上报。
 *
 * 这个枚举没有 isXxx 守卫(全仓只有 AnchorageScope 这一个类型标记),
 * 所以下面是唯一一处不走 isXxx 的标签函数;判断条件仍然是同一个数组。
 */
export const ANCHORAGE_SCOPE_LABELS: Record<AnchorageScope, string> = {
  inside: '锚地内',
  outside: '锚地外',
}

export function anchorageScopeLabel(value: string): string {
  const hit = ANCHORAGE_SCOPE.find((x) => x === value)
  return hit === undefined ? value : ANCHORAGE_SCOPE_LABELS[hit]
}

/** 沿用承运商页原来那三个词(「待审 / 已入驻 / 已拒绝」),不另起译名 */
export const CARRIER_APPLICATION_STATUS_LABELS: Record<CarrierApplicationStatus, string> = {
  pending: '待审',
  approved: '已入驻',
  rejected: '已拒绝',
}

export function carrierApplicationStatusLabel(value: string): string {
  return isCarrierApplicationStatus(value) ? CARRIER_APPLICATION_STATUS_LABELS[value] : value
}

/**
 * 起飞申请状态(S-51)。待审核 / 已通过 / 已驳回 —— 三个词取自
 * shared 的 enums/takeoffApplicationStatus.ts 文件头与契约 TakeoffApplicationStatus
 * 的 description(「待审核 / 已通过 / 已驳回。三态,与承运商入驻审核同形」)。
 *
 * 监管端的起飞申请列表与详情、小程序的作业详情页「起飞申请」区块都从这张表取词。
 * 与承运商申请那三个词有意不同(那里是「待审 / 已入驻 / 已拒绝」):那一条的通过
 * 落地动作是「建一家承运商公司」,这一条的通过只是「这一趟准飞」—— 两个「通过」
 * 不是同一件事,用同一个词会让监管端把「准飞」读成「公司入驻」。
 */
export const TAKEOFF_APPLICATION_STATUS_LABELS: Record<TakeoffApplicationStatus, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已驳回',
}

export function takeoffApplicationStatusLabel(value: string): string {
  return isTakeoffApplicationStatus(value) ? TAKEOFF_APPLICATION_STATUS_LABELS[value] : value
}

/**
 * 入驻材料的类型(材料带类型,2026-10-11)。两个词取自申请页那两个上传位(S-24 就写给申请人看了)
 * 与 M-27 的原文「营业执照 / 运输资质」—— 不另起一套。
 *
 * 审核人靠这一列知道哪张是营业执照;申请页的上传位也从这张表取标签,
 * 于是「申请人上传时看到的词」与「审核人核对时看到的词」是同一份。
 * 手写一份的下一步就是 `type === 'business_license' ? '营业执照' : '资质'`——
 * 那会把认不出来的取值说成「资质」,而「资质」看起来像一个正常的答案。
 */
export const CARRIER_MATERIAL_TYPE_LABELS: Record<CarrierMaterialType, string> = {
  business_license: '营业执照',
  transport_permit: '运输资质',
}

export function carrierMaterialTypeLabel(value: string): string {
  return isCarrierMaterialType(value) ? CARRIER_MATERIAL_TYPE_LABELS[value] : value
}

/** 沿用计价表页原来那三个词(「草稿 / 发布中 / 已归档」) */
export const PRICING_TABLE_STATUS_LABELS: Record<PricingTableStatus, string> = {
  draft: '草稿',
  published: '发布中',
  archived: '已归档',
}

export function pricingTableStatusLabel(value: string): string {
  return isPricingTableStatus(value) ? PRICING_TABLE_STATUS_LABELS[value] : value
}

/**
 * 后台账号类别。两个端原来分别显示「监管端」与原始码 `ops` ——
 * 有了这张表,「运营」与「监管端」是同一个决定的两个词,不会各写各的。
 */
export const ADMIN_ACCOUNT_KIND_LABELS: Record<AdminAccountKind, string> = {
  ops: '运营',
  gov: '监管端',
}

export function adminAccountKindLabel(value: string): string {
  return isAdminAccountKind(value) ? ADMIN_ACCOUNT_KIND_LABELS[value] : value
}

/**
 * 证件类型(S-22 加)。
 *
 * 它此前在 enum-labels 的**豁免表**里,理由原文是:「要显示时那张表该与
 * 小程序的录入表单一起进共享包」。S-22 的实名资料页就是那个录入表单 ——
 * 所以这一条从豁免表挪进了登记表,表也落在这里(与它的函数一起)。
 *
 * 三个词取自 shared 的 enums/idType.ts 文件头(「身份证或护照或其他」)——
 * 那是这组译名第一次出现的地方,不另起一套。
 */
export const ID_TYPE_LABELS: Record<IdType, string> = {
  id_card: '身份证',
  passport: '护照',
  other: '其他',
}

export function idTypeLabel(value: string): string {
  return isIdType(value) ? ID_TYPE_LABELS[value] : value
}

// ───────────────────────────────────────────────────────
// 后两组状态的颜色分组(照 status.ts 的 statusGroup 写)
//
// 承运商申请与计价表的状态原来各自用 n-tag 挑一个类型(pending → warning、
// draft → warning 之类)。问题不是颜色不对,是**它在每个视图里各挑一次** ——
// 两个页面对同一个取值挑出两种颜色时,页面上没有任何东西会说出来。
// 所以分组表也收在这里,视图只问「这个取值是哪一组」。
// ───────────────────────────────────────────────────────

/**
 * pending 是「等我们动手」→ attention(橙);approved 是终态的好 → done(绿);
 * rejected 与订单的 cancelled / refunded 同一档 → draft(灰)。
 *
 * 灰色说的是「这一条不再需要谁做什么」。红色是留给出错的那一类的,
 * 用它画一个已经审完的拒绝会让运营以为还有一步要被处理。
 */
export const CARRIER_APPLICATION_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  pending: 'attention',
  approved: 'done',
  rejected: 'draft',
}

export function carrierApplicationStatusGroup(value: string): StatusGroup {
  return CARRIER_APPLICATION_STATUS_GROUPS[value] ?? 'draft'
}

/**
 * 起飞申请状态的颜色分组(S-51,照 CARRIER_APPLICATION_STATUS_GROUPS 写)。
 *
 * pending 是「等监管端动手」→ attention(橙);approved 是终态的好 → done(绿);
 * rejected 与订单的 cancelled / refunded 同一档 → draft(灰)——「这一条不再需要谁做什么」。
 * 监管端列表的状态列与小程序作业页的申请区块都从这里取组,不各挑一次色。
 */
export const TAKEOFF_APPLICATION_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  pending: 'attention',
  approved: 'done',
  rejected: 'draft',
}

export function takeoffApplicationStatusGroup(value: string): StatusGroup {
  return TAKEOFF_APPLICATION_STATUS_GROUPS[value] ?? 'draft'
}

/**
 * draft 是「草稿,等着有人发布它」→ attention(橙);published 是正在生效的那一版 → done(绿);
 * archived 是历史版本 → draft(灰)。三组的取法与前一组同一个道理。
 */
export const PRICING_TABLE_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  draft: 'attention',
  published: 'done',
  archived: 'draft',
}

export function pricingTableStatusGroup(value: string): StatusGroup {
  return PRICING_TABLE_STATUS_GROUPS[value] ?? 'draft'
}
